import { Editor } from "@tiptap/react";
import {
  Copy,
  ExternalLink,
  Link2Off,
  Pencil,
} from "lucide-react";
import { FC, useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface LinkBubbleMenuProps {
  editor: Editor | null;
}

const normalizeLinkHref = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const truncateUrl = (url: string, maxLength: number = 40): string => {
  if (url.length <= maxLength) return url;
  return url.slice(0, maxLength - 3) + "...";
};

const BUBBLE_OFFSET = 8;

export const LinkBubbleMenu: FC<LinkBubbleMenuProps> = ({ editor }) => {
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [linkDraftValue, setLinkDraftValue] = useState("");
  const [bubbleRect, setBubbleRect] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const rafRef = useRef<number | null>(null);
  const prevActiveRef = useRef(false);

  const currentLinkHref =
    editor && typeof editor.getAttributes("link").href === "string"
      ? editor.getAttributes("link").href
      : "";

  const updatePosition = useCallback(() => {
    if (!editor || !editor.isActive("link")) {
      if (prevActiveRef.current) {
        prevActiveRef.current = false;
        setBubbleRect(null);
      }
      return;
    }
    prevActiveRef.current = true;
    const { view } = editor;
    const { from } = editor.state.selection;
    const coords = view.coordsAtPos(from);
    const editorRect = view.dom.getBoundingClientRect();
    const top = Math.max(
      editorRect.top,
      coords.top - BUBBLE_OFFSET,
    );
    const left = Math.min(
      Math.max(editorRect.left, coords.left),
      editorRect.right - 300,
    );
    setBubbleRect({ top, left });
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    const scheduleUpdate = () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        updatePosition();
      });
    };
    editor.on("selectionUpdate", scheduleUpdate);
    editor.on("transaction", scheduleUpdate);
    return () => {
      editor.off("selectionUpdate", scheduleUpdate);
      editor.off("transaction", scheduleUpdate);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [editor, updatePosition]);

  const handleCopy = useCallback(() => {
    if (!currentLinkHref) return;
    navigator.clipboard.writeText(currentLinkHref);
  }, [currentLinkHref]);

  const handleEdit = useCallback(() => {
    setLinkDraftValue(currentLinkHref);
    setIsEditingLink(true);
  }, [currentLinkHref]);

  const handleApplyLink = useCallback(() => {
    if (!editor) return;
    const href = normalizeLinkHref(linkDraftValue);
    if (!href) return;
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href })
      .run();
    setIsEditingLink(false);
  }, [editor, linkDraftValue]);

  const handleUnlink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setIsEditingLink(false);
  }, [editor]);

  const handleOpen = useCallback(() => {
    if (!currentLinkHref) return;
    window.open(currentLinkHref, "_blank", "noopener,noreferrer");
  }, [currentLinkHref]);

  const handleCancelEdit = useCallback(() => {
    setIsEditingLink(false);
    setLinkDraftValue(currentLinkHref);
  }, [currentLinkHref]);

  if (!editor || !editor.isActive("link") || !bubbleRect) return null;

  const bubbleContent = (
    <div
      className="flex items-center gap-2 rounded-lg border border-gray-600 bg-gray-800 px-2 py-1.5 text-xs text-gray-200 shadow-lg"
      style={{
        position: "fixed",
        top: bubbleRect.top,
        left: bubbleRect.left,
        transform: "translateY(-100%)",
        zIndex: 9999,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {isEditingLink ? (
        <>
          <input
            type="text"
            value={linkDraftValue}
            onChange={(e) => setLinkDraftValue(e.target.value)}
            className="w-48 rounded border border-gray-600 bg-gray-900 px-2 py-1 text-xs text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="https://example.com"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleApplyLink();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                handleCancelEdit();
              }
            }}
          />
          <button
            type="button"
            className="rounded px-2 py-1 text-blue-300 hover:bg-blue-500/20"
            onClick={handleApplyLink}
            title="Apply"
          >
            Apply
          </button>
          <button
            type="button"
            className="rounded p-1 text-gray-300 hover:bg-gray-700"
            onClick={handleCancelEdit}
            title="Cancel"
          >
            Cancel
          </button>
        </>
      ) : (
        <>
          <span
            className="max-w-[200px] truncate text-gray-300"
            title={currentLinkHref}
          >
            {truncateUrl(currentLinkHref)}
          </span>
          <button
            type="button"
            className="rounded p-1 text-gray-300 hover:bg-gray-700"
            onClick={handleCopy}
            title="Copy URL"
          >
            <Copy className="size-3.5" />
          </button>
          <button
            type="button"
            className="rounded p-1 text-gray-300 hover:bg-gray-700"
            onClick={handleEdit}
            title="Edit link"
          >
            <Pencil className="size-3.5" />
          </button>
          <button
            type="button"
            className="rounded p-1 text-gray-300 hover:bg-gray-700"
            onClick={handleUnlink}
            title="Unlink"
          >
            <Link2Off className="size-3.5" />
          </button>
          <button
            type="button"
            className="rounded p-1 text-gray-300 hover:bg-gray-700"
            onClick={handleOpen}
            title="Open in new tab"
          >
            <ExternalLink className="size-3.5" />
          </button>
        </>
      )}
    </div>
  );

  return createPortal(bubbleContent, document.body);
};
